import { CHUNK_SIZE } from "@craftgaming/shared";

const VERTICAL_CHUNKS = 6; // Y levels 0-5
const MAX_LOADS_PER_FRAME = 3; // spread load across frames
const MAX_RENDER_DISTANCE = 10; // slider upper bound — unload scans use this

export type ChunkLoaderCallbacks = {
  loadChunk: (cx: number, cy: number, cz: number) => void;
  unloadChunk: (cx: number, cy: number, cz: number) => void;
  isChunkLoaded: (cx: number, cy: number, cz: number) => boolean;
  /** Player-configurable render distance, in chunks. */
  getRenderDistance: () => number;
};

export type ChunkLoader = {
  update: (playerX: number, playerZ: number) => void;
  /** Re-evaluate loaded chunks now (e.g. after the render distance changes). */
  forceRecalculate: () => void;
};

export function createChunkLoader(callbacks: ChunkLoaderCallbacks): ChunkLoader {
  let lastPlayerCX = Infinity;
  let lastPlayerCZ = Infinity;
  const loadQueue: Array<{ cx: number; cy: number; cz: number }> = [];

  function update(playerX: number, playerZ: number) {
    const playerCX = Math.floor(playerX / CHUNK_SIZE);
    const playerCZ = Math.floor(playerZ / CHUNK_SIZE);

    // Only recalculate when player moves to new chunk
    if (playerCX !== lastPlayerCX || playerCZ !== lastPlayerCZ) {
      lastPlayerCX = playerCX;
      lastPlayerCZ = playerCZ;
      recalculateChunks(playerCX, playerCZ);
    }

    // Process load queue (spread across frames)
    let loaded = 0;
    while (loadQueue.length > 0 && loaded < MAX_LOADS_PER_FRAME) {
      const { cx, cy, cz } = loadQueue.shift()!;
      if (!callbacks.isChunkLoaded(cx, cy, cz)) {
        callbacks.loadChunk(cx, cy, cz);
        loaded++;
      }
    }
  }

  function forceRecalculate() {
    lastPlayerCX = Infinity;
    lastPlayerCZ = Infinity;
  }

  function recalculateChunks(playerCX: number, playerCZ: number) {
    const renderDistance = Math.max(2, callbacks.getRenderDistance());
    const unloadDistance = renderDistance + 2;

    // Queue chunks to load (sorted by distance — nearest first)
    loadQueue.length = 0;

    const toLoad: Array<{ cx: number; cy: number; cz: number; dist: number }> = [];

    for (let dx = -renderDistance; dx <= renderDistance; dx++) {
      for (let dz = -renderDistance; dz <= renderDistance; dz++) {
        // Circular distance check
        const dist = Math.sqrt(dx * dx + dz * dz);
        if (dist > renderDistance) continue;

        const cx = playerCX + dx;
        const cz = playerCZ + dz;

        for (let cy = 0; cy < VERTICAL_CHUNKS; cy++) {
          if (!callbacks.isChunkLoaded(cx, cy, cz)) {
            toLoad.push({ cx, cy, cz, dist });
          }
        }
      }
    }

    // Sort nearest first
    toLoad.sort((a, b) => a.dist - b.dist);
    for (const item of toLoad) {
      loadQueue.push({ cx: item.cx, cy: item.cy, cz: item.cz });
    }

    // Unload distant chunks — scan out to the LARGEST radius that could
    // ever have been loaded, otherwise shrinking the render distance at
    // runtime orphans chunks beyond the new (smaller) scan bound.
    const scanBound = MAX_RENDER_DISTANCE + 4;
    for (let dx = -scanBound; dx <= scanBound; dx++) {
      for (let dz = -scanBound; dz <= scanBound; dz++) {
        const dist = Math.sqrt(dx * dx + dz * dz);
        if (dist <= unloadDistance) continue;

        const cx = playerCX + dx;
        const cz = playerCZ + dz;

        for (let cy = 0; cy < VERTICAL_CHUNKS; cy++) {
          if (callbacks.isChunkLoaded(cx, cy, cz)) {
            callbacks.unloadChunk(cx, cy, cz);
          }
        }
      }
    }
  }

  return { update, forceRecalculate };
}
